/**
 * Centralized {label, tone} maps for every domain enum the web UI renders.
 *
 * Each enum's UI rule lives in exactly one place — adding a value to an
 * enum forces an exhaustiveness error here, so the rendering rule cannot
 * silently fall behind the type. Components import the map they need and
 * read `<Pill tone={X_UI[v].tone}>{X_UI[v].label}</Pill>`.
 *
 * Out of scope: open-set string-keyed status maps (e.g. BootstrapPage
 * source/signal status) — those use `?? "neutral"` fallbacks because
 * their key set isn't bounded by a TS enum and don't fit this shape.
 * `CrmAdvisorSignalStatus` is also out of scope today: its component
 * renders the raw enum literal, not a label.
 */

import type { PillTone } from "../components/Pill";
import type { EventGroup } from "../projects/timeline/TimelineView";
import type {
  CompletenessStatus,
  CompletenessVerdict,
  DiscrepancySeverity,
  DiscrepancyStatus,
  DiscrepancyType,
  MatchStatus,
  TruthSourceType,
} from "../projects/types";
import type {
  PaymentAttachmentClassificationStatus,
  PaymentProjectLinkStatus,
  PaymentValidationStatus,
} from "../payment_orders/types";
import type {
  ReviewItemSeverity,
  ReviewItemSource,
  ReviewItemStatus,
} from "../review/types";

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export const COMPLETENESS_STATUS_UI: Record<
  CompletenessStatus,
  { label: string; tone: PillTone }
> = {
  unknown: { label: "In progress", tone: "neutral" },
  incomplete: { label: "Incomplete", tone: "danger" },
  complete: { label: "Complete", tone: "success" },
  review_required: { label: "Review required", tone: "warning" },
};

export const COMPLETENESS_VERDICT_UI: Record<
  CompletenessVerdict,
  { label: string; tone: PillTone }
> = {
  complete: { label: "Complete", tone: "success" },
  incomplete: { label: "Incomplete", tone: "danger" },
  review_required: { label: "Review required", tone: "warning" },
};

export const TRUTH_SOURCE_UI: Record<TruthSourceType, { label: string }> = {
  procurement_request: { label: "Procurement request" },
  contract: { label: "Contract" },
  override: { label: "Override" },
};

export const MATCH_STATUS_UI: Record<
  MatchStatus,
  { label: string; tone: PillTone }
> = {
  match: { label: "Match", tone: "success" },
  mismatch: { label: "Mismatch", tone: "danger" },
  inconclusive: { label: "Inconclusive", tone: "warning" },
  not_applicable: { label: "N/A", tone: "neutral" },
};

export const DISCREPANCY_TYPE_UI: Record<DiscrepancyType, { label: string }> = {
  missing_required_document: { label: "Missing required document" },
  wrong_type_for_requirement: { label: "Wrong type for requirement" },
  cross_check_mismatch: { label: "Cross-check mismatch" },
  payment_support_pack_incomplete: { label: "Payment support-pack incomplete" },
  ambiguous_project_link: { label: "Ambiguous project link" },
  reappeared_after_delete: { label: "Reappeared after delete" },
  override_pending_review: { label: "Override pending review" },
};

export const DISCREPANCY_STATUS_UI: Record<
  DiscrepancyStatus,
  { label: string; tone: PillTone }
> = {
  open: { label: "Open", tone: "danger" },
  review_required: { label: "Review required", tone: "warning" },
  resolved: { label: "Resolved", tone: "success" },
  suppressed: { label: "Suppressed", tone: "neutral" },
  reopened: { label: "Reopened", tone: "warning" },
};

export const DISCREPANCY_SEVERITY_UI: Record<
  DiscrepancySeverity,
  { label: string; tone: PillTone }
> = {
  deficiency: { label: "Deficiency", tone: "warning" },
  material_weakness: { label: "Material weakness", tone: "danger" },
  informational: { label: "Informational", tone: "neutral" },
};

export const EVENT_GROUP_UI: Record<
  EventGroup,
  { label: string; tone: PillTone }
> = {
  file: { label: "File", tone: "neutral" },
  payment_order: { label: "Payment order", tone: "info" },
  discrepancy: { label: "Discrepancy", tone: "warning" },
  project: { label: "Project", tone: "info" },
  other: { label: "Event", tone: "neutral" },
};

// ---------------------------------------------------------------------------
// Payment orders
// ---------------------------------------------------------------------------

export const PAYMENT_LINK_STATUS_UI: Record<
  PaymentProjectLinkStatus,
  { label: string; tone: PillTone }
> = {
  unlinked: { label: "Unlinked", tone: "neutral" },
  ambiguous: { label: "Ambiguous", tone: "warning" },
  linked: { label: "Linked", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
};

export const PAYMENT_VALIDATION_STATUS_UI: Record<
  PaymentValidationStatus,
  { label: string; tone: PillTone }
> = {
  validated: { label: "Validated", tone: "success" },
  review_required: { label: "Review required", tone: "warning" },
  invoice_missing: { label: "Invoice missing", tone: "danger" },
  support_pack_incomplete: { label: "Support-pack incomplete", tone: "danger" },
  project_unknown: { label: "Project unknown", tone: "neutral" },
};

export const PAYMENT_ATTACHMENT_CLASSIFICATION_UI: Record<
  PaymentAttachmentClassificationStatus,
  { label: string; tone: PillTone }
> = {
  unclassified: { label: "Unclassified", tone: "warning" },
  invoice: { label: "Invoice", tone: "info" },
  bill_of_lading: { label: "Bill of lading", tone: "info" },
  contract: { label: "Contract", tone: "info" },
  procurement_request: { label: "Procurement request", tone: "info" },
  certificate: { label: "Certificate", tone: "info" },
  other: { label: "Other", tone: "neutral" },
  rejected_as_wrong_type: { label: "Rejected (wrong type)", tone: "danger" },
};

// ---------------------------------------------------------------------------
// Review queue
// ---------------------------------------------------------------------------

export const REVIEW_SOURCE_UI: Record<ReviewItemSource, { label: string }> = {
  ambiguous_payment_link: { label: "Ambiguous payment link" },
  classification_review: { label: "Classification review" },
  extraction_review: { label: "Extraction review" },
  wrong_type_rejection: { label: "Wrong-type rejection" },
  cross_check_mismatch: { label: "Cross-check mismatch" },
};

export const REVIEW_SEVERITY_UI: Record<
  ReviewItemSeverity,
  { label: string; tone: PillTone }
> = {
  deficiency: { label: "Deficiency", tone: "warning" },
  material_weakness: { label: "Material weakness", tone: "danger" },
  informational: { label: "Informational", tone: "neutral" },
};

export const REVIEW_STATUS_UI: Record<
  ReviewItemStatus,
  { label: string; tone: PillTone }
> = {
  open: { label: "Open", tone: "warning" },
  resolved: { label: "Resolved", tone: "success" },
  suppressed: { label: "Suppressed", tone: "neutral" },
};
